import { useTheme, type Theme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { dayjsWithExtensions as dayjs } from '../../utils/dayjs-config';
import { pagesConfig } from '../../shared/pages-config';
import {
  FULL_WIDTH_TILE_COLUMNS,
  HALF_WIDTH_TILE_COLUMNS,
  SECONDARY_STAT_LABEL_VALUE_GAP,
  SECONDARY_STAT_PADDING,
  TILE_CONTENT_PADDING,
  STATS_ROW_GAP_FLOOR,
  WIDE_TILE_REGIONS_GAP,
  wideTileRegionSx,
} from './tile-config';
import type { CommandDashboardTileBodyProps } from './tile-content-registry';
import TileInsufficientAccessNotice from './tile-insufficient-access-notice';
import { useCanSeeTile } from './use-tile-access';
import { useGroupsAndAccessTileMetrics } from './use-groups-and-access-tile-metrics';
import { useBindTileRefresh } from './use-tile-refresh';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowForward from '@mui/icons-material/ArrowForward';
import FolderSharedOutlined from '@mui/icons-material/FolderSharedOutlined';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import type SvgIcon from '@mui/material/SvgIcon';

type SvgIconComponent = typeof SvgIcon;

const CARDS_GAP = 24; // gap between the 2 hero cards at every tier (Figma: 383-359=24)
const HERO_CARD_HEIGHT = 144;
const SECONDARY_STATS_GAP = 0; // items sit contiguous in Figma - spacing comes from each item's own padding

/**
 * The three layouts, one per size the tile can be placed at.
 *
 * Picked from the columns the grid gave the tile rather than from its rendered
 * width: a dashboard narrower than Figma's 1622px canvas draws every size
 * proportionally smaller, so a pixel width cannot tell a half-width tile from a
 * third-width one, but the span always can.
 */
type LayoutTier = 'wide' | 'compact' | 'narrow';

function layoutTierFor(columns: number): LayoutTier {
  if (columns >= FULL_WIDTH_TILE_COLUMNS) {
    return 'wide';
  }
  return columns >= HALF_WIDTH_TILE_COLUMNS ? 'compact' : 'narrow';
}

type HeroCardColor = 'blue' | 'violet';

function metricValue(
  isFetching: boolean,
  value: number | undefined
): ReactNode {
  return isFetching ? <Skeleton width={40} /> : value ?? 0;
}

interface HeroStatCardProps {
  icon: SvgIconComponent;
  label: string;
  value: ReactNode;
  color: HeroCardColor;
  onNavigate: () => void;
  navigateLabel: string;
}

function HeroStatCard({
  icon: Icon,
  label,
  value,
  color,
  onNavigate,
  navigateLabel,
}: HeroStatCardProps) {
  const theme = useTheme();
  const bg =
    color === 'blue'
      ? theme.palette.semantic.card.info
      : theme.palette.semantic.card.violet;
  const text =
    color === 'blue'
      ? theme.palette.semantic.metricText.info
      : theme.palette.semantic.metricText.violet;
  return (
    <Stack
      sx={{
        display: 'flex',
        minHeight: `${HERO_CARD_HEIGHT}px`,
        padding: '24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        flex: '1 1 0',
        minWidth: 0,
        borderRadius: '4px',
        bgcolor: bg,
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}
      >
        <Icon sx={{ width: 24, height: 24, color: text, flexShrink: 0 }} />
        <Typography variant="body1" sx={{ color: text, minWidth: 0 }}>
          {label}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="h3" sx={{ color: text, fontWeight: 400 }}>
          {value}
        </Typography>
        <IconButton
          aria-label={navigateLabel}
          onClick={onNavigate}
          sx={{
            bgcolor: (theme) => theme.palette.common.white,
            boxShadow: 1,
            flexShrink: 0,
          }}
        >
          <ArrowForward sx={{ color: text }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

interface SecondaryStatProps {
  label: string;
  value: ReactNode;
  tier: LayoutTier;
}

function SecondaryStat({ label, value, tier }: SecondaryStatProps) {
  return (
    <Stack
      sx={{
        gap: `${SECONDARY_STAT_LABEL_VALUE_GAP}px`,
        padding: SECONDARY_STAT_PADDING,
        ...(tier !== 'narrow' && {
          flex: '1 1 0',
          minWidth: 0,
          justifyContent: 'space-between',
        }),
        ...(tier === 'narrow' && {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          minWidth: 0,
          flex: 1,
          minHeight: 0,
          padding: '16px 8px',
          borderBottom: (theme: Theme) =>
            `0.5px solid ${theme.palette.semantic.outline.light}`,
        }),
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: (theme) => theme.palette.semantic.text.title2,
          ...(tier !== 'narrow' && {
            width: '100%',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
          ...(tier === 'narrow' && { whiteSpace: 'nowrap' }),
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '34px',
          lineHeight: 1.235,
          letterSpacing: '0.25px',
          color: (theme) => theme.palette.semantic.stepper.active,
          ...(tier === 'narrow' && {
            fontSize: '24px',
            lineHeight: 1.334,
            letterSpacing: 0,
          }),
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function GroupsAndAccessTileMetrics({
  tile,
  columns = FULL_WIDTH_TILE_COLUMNS,
}: CommandDashboardTileBodyProps) {
  const tier = layoutTierFor(columns);
  const hasPermission = useCanSeeTile(tile);
  const navigate = useNavigate();
  const { metrics, lastUpdated, isFetching, isRefreshing, onRefresh } =
    useGroupsAndAccessTileMetrics(hasPermission);
  useBindTileRefresh(tile, hasPermission, {
    onRefresh,
    isFetching: isRefreshing,
  });

  if (!hasPermission && metrics === undefined) {
    return <TileInsufficientAccessNotice tile={tile} />;
  }

  return (
    <Box
      data-testid="groups-and-access-metrics"
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: `${TILE_CONTENT_PADDING}px`,
      }}
    >
      {(() => {
        const cardsRow = (
          <Stack
            sx={{
              gap: `${CARDS_GAP}px`,
              flexDirection: tier === 'narrow' ? 'column' : 'row',
              minWidth: 0,
              ...(tier === 'wide' && wideTileRegionSx),
            }}
          >
            <HeroStatCard
              icon={GroupAddOutlined}
              label="Total Group Count"
              value={metricValue(isFetching, metrics?.totalGroups)}
              color="blue"
              navigateLabel="View Groups"
              onNavigate={() => navigate(`/${pagesConfig.groups.link}`)}
            />
            <HeroStatCard
              icon={FolderSharedOutlined}
              label="Total Shared Drive Count"
              value={metricValue(isFetching, metrics?.totalSharedDrives)}
              color="violet"
              navigateLabel="View Shared Drives"
              onNavigate={() => navigate(`/${pagesConfig.sharedDrives.link}`)}
            />
          </Stack>
        );

        const caption = (
          <Typography
            variant="caption"
            sx={{
              pl: '8px',
              color: (theme) => theme.palette.semantic.text.support,
            }}
          >
            Last Updated:{' '}
            {lastUpdated
              ? dayjs(lastUpdated * 1000).format('MMM D, YYYY, h:mm A')
              : '--'}
          </Typography>
        );

        const secondaryStats = (
          <Stack
            sx={{
              gap: `${SECONDARY_STATS_GAP}px`,
              ...(tier === 'wide' && {
                flexDirection: 'row',
                ...wideTileRegionSx,
              }),
              ...(tier === 'compact' && {
                flexDirection: 'row',
                justifyContent: 'space-between',
                flex: '0 0 auto',
                width: '100%',
                minWidth: 0,
              }),
              ...(tier === 'narrow' && {
                flexDirection: 'column',
                flex: '1 1 auto',
                width: '100%',
                minWidth: 0,
              }),
            }}
          >
            <SecondaryStat
              tier={tier}
              label="Groups With No Owner"
              value={metricValue(isFetching, metrics?.groupsWithNoOwner)}
            />
            <SecondaryStat
              tier={tier}
              label="Groups With External Members"
              value={metricValue(
                isFetching,
                metrics?.groupsWithExternalMembers
              )}
            />
            <SecondaryStat
              tier={tier}
              label="Shared Drives With No Manager"
              value={metricValue(
                isFetching,
                metrics?.sharedDrivesWithNoManager
              )}
            />
          </Stack>
        );

        if (tier === 'wide') {
          return (
            <Stack
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                gap: '16px',
                justifyContent: 'center',
              }}
            >
              <Stack
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: `${WIDE_TILE_REGIONS_GAP}px`,
                  minWidth: 0,
                }}
              >
                {cardsRow}
                {secondaryStats}
              </Stack>
              {caption}
            </Stack>
          );
        }

        return (
          <Stack
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: tier === 'compact' ? 'space-between' : undefined,
              gap: `${STATS_ROW_GAP_FLOOR}px`,
            }}
          >
            <Stack
              sx={{ gap: '16px', flex: '0 0 auto', width: '100%', minWidth: 0 }}
            >
              {cardsRow}
              {caption}
            </Stack>
            {secondaryStats}
          </Stack>
        );
      })()}
    </Box>
  );
}
